import { useMutation, useQueryClient } from "@tanstack/react-query";
import ky from "ky";
import type {
  CreateAccountFormData,
  CreateAccountResponse,
  ServiceAccountCreationRequest
} from "@dealer-center/ddc-pages-account-search-interface";

const CREATE_ACCOUNT_QUERY_KEY = "ddc-pages-account-search";
const CREATE_ACCOUNT_ENDPOINT = "/landing/nxlambda/accountSearch/create";

// Map CreateAccountFormData to ServiceAccountCreationRequest
export const mapFormDataToServiceRequest = (
  formData: CreateAccountFormData
): ServiceAccountCreationRequest => {
  return {
    dealerCode: null,
    companyName: formData.companyName.trim(),
    program: null,
    programGroupType: null,
    status: "IN_PRODUCTION",
    type: formData.accountType,
    verticalType: formData.verticalType,
    franchiseNames: null,
    primaryAddress: {
      address1: formData.address1.trim() || null,
      address2: formData.address2?.trim() || null,
      city: formData.city?.trim() || null,
      state: formData.state?.trim() || null,
      postalCode: formData.zipCode?.trim() || null,
      country: formData.country?.trim() || null
    },
    requestedServiceAccountId: null,
    email: null,
    workPhone: null
  };
};

/**
 * Call the Lambda endpoint to create a new service account
 */
export const createAccount = async (
  request: ServiceAccountCreationRequest
): Promise<CreateAccountResponse> => {
  try {
    const response = await ky.post(CREATE_ACCOUNT_ENDPOINT, {
      json: request
    });
    return response.json<CreateAccountResponse>();
  } catch (error) {
    // If it's a ky HTTPError, try to extract the server's error message
    if (error && typeof error === "object" && "response" in error) {
      const httpError = error as { response: Response };
      try {
        const errorData = await httpError.response.json();
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      } catch (parseError) {
        // If we can't parse the response, create a generic error without the URL
        const status = httpError.response.status;
        const statusText = httpError.response.statusText || "Error";
        throw new Error(
          `Request failed with status code ${status} ${statusText}`
        );
      }
    }

    // For non-HTTP errors, throw a generic message
    if (error instanceof Error) {
      throw new Error("Failed to create account. Please try again.");
    }

    throw new Error("An unexpected error occurred");
  }
};

export const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateAccountResponse, Error, CreateAccountFormData>({
    mutationFn: (formData: CreateAccountFormData) => {
      const serviceRequest = mapFormDataToServiceRequest(formData);
      return createAccount(serviceRequest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CREATE_ACCOUNT_QUERY_KEY]
      });
    },
    onError: error => {
      console.error("Create Account Error:", error);
    }
  });
};
